import { Button, ToggleButton } from '@tunasong/ui-lib'
import type { FC } from 'react'
import styles from './melody-transport.module.css'

/**
 * The melody editor's transport (melody toolkit plan M2 step 2.4; spec §5.1, §5.2), left to right in
 * the order of the journey: hear, then put notes in. `Chords` and `Melody` are both on by default, so
 * playback is the line over the changes; with the melody empty, `Chords only` plays the progression to
 * sing over. `Sing an idea` is the primary action.
 *
 * Below 600px it is a bar pinned to the bottom of the edit view, inside the layout (§9).
 */

export interface MelodyTransportProps {
  isPlaying: boolean
  onPlay: () => void
  onStop: () => void
  isLooping: boolean
  onLoopChange: (isLooping: boolean) => void
  bpm: number
  /** The melody has notes: without them there is no Melody toggle, and Play becomes Chords only */
  hasNotes: boolean
  /** The lane has chords: without them there is no Chords toggle */
  hasChords: boolean
  isChordsOn: boolean
  onChordsChange: (isChordsOn: boolean) => void
  isMelodyOn: boolean
  onMelodyChange: (isMelodyOn: boolean) => void
  onSingIdea: () => void
  /** The standalone sketch opens with Sing an idea focused (melody toolkit M9, spec §7) */
  autoFocusSing?: boolean
  className?: string
}

export const MelodyTransport: FC<MelodyTransportProps> = props => {
  const {
    isPlaying,
    onPlay,
    onStop,
    isLooping,
    onLoopChange,
    bpm,
    hasNotes,
    hasChords,
    isChordsOn,
    onChordsChange,
    isMelodyOn,
    onMelodyChange,
    onSingIdea,
    autoFocusSing = false,
    className,
  } = props
  const playLabel = !hasNotes && hasChords ? 'Chords only' : 'Play'

  return (
    <div className={className ? `${styles.transport} ${className}` : styles.transport}>
      <Button onClick={isPlaying ? onStop : onPlay}>{isPlaying ? 'Stop' : playLabel}</Button>
      <ToggleButton value="loop" size="small" selected={isLooping} onClick={() => onLoopChange(!isLooping)}>
        Loop
      </ToggleButton>
      <span className={styles.tempo}>{`${bpm} BPM`}</span>
      {hasChords ? (
        <ToggleButton value="chords" size="small" selected={isChordsOn} onClick={() => onChordsChange(!isChordsOn)}>
          Chords
        </ToggleButton>
      ) : null}
      {hasNotes ? (
        <ToggleButton value="melody" size="small" selected={isMelodyOn} onClick={() => onMelodyChange(!isMelodyOn)}>
          Melody
        </ToggleButton>
      ) : null}
      <Button variant="contained" className={styles.sing} onClick={onSingIdea} autoFocus={autoFocusSing}>
        Sing an idea
      </Button>
    </div>
  )
}
